import React, { useState, useEffect } from "react";
import {
  VStack,
  Text,
  Button,
  Alert,
  AlertDescription,
  Flex,
  Icon,
  Spinner,
} from "@chakra-ui/react";
import { FiPieChart } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { $Routes } from "@routes";
import { useGetAgendaReportQuery } from "@store/api/report";
import {
  useGetPastAgendasForReportQuery,
  useDuplicateAgendaMutation,
} from "@store/api/agenda";
import { useProfile } from "@hooks";
import { ReportsStats } from "@components/Reports";
import { AgendaList, AgendaModal } from "@components/Agenda";
import { AgendaHeader } from "@components/Agenda";
import { showSuccessToast, showErrorToast } from "@store/customActions";
import duplicateIcon from "@assets/svg/icon-duplicate.svg";

/**
 * ReportsView displays the main Reports page with stats and past agendas
 * Shows E-Score, Average Impact Score, and past agendas with impact scores
 */
const ReportsView = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { profile } = useProfile();
  const [reportStats, setReportStats] = useState({
    eScore: 0,
    maxImpactScore: 0,
    totalAgendas: 0,
  });

  // Duplicate modal state
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [selectedAgendaId, setSelectedAgendaId] = useState("");

  // Fetch past agendas with impact scores directly from new endpoint
  const { data: pastAgendasResponse, isLoading: isLoadingAgendas } =
    useGetPastAgendasForReportQuery(undefined, { skip: !profile?._id });

  // Extract past agendas from the response
  const pastAgendas = pastAgendasResponse?.pastAgendas || [];

  // Fetch report data for stats calculation
  const { data: reportData, isLoading: isLoadingReport } =
    useGetAgendaReportQuery(
      { userId: profile?._id },
      { skip: !profile?._id || pastAgendas.length === 0 }
    );

  // Duplicate agenda mutation
  const [duplicateAgenda, { isLoading: isDuplicatingAgenda }] =
    useDuplicateAgendaMutation();

  // Calculate stats when report data is available
  useEffect(() => {
    if (reportData && pastAgendas.length > 0) {
      // Calculate average E-Score from past agendas
      const totalEScore = pastAgendas.reduce((sum, agenda) => {
        const agendaReport = Array.isArray(reportData)
          ? reportData.find((report) => report.agendaId === agenda._id)
          : reportData.agendaId === agenda._id
          ? reportData
          : null;
        return sum + (agendaReport?.eScore || 0);
      }, 0);

      // Calculate maximum Impact Score from past agendas
      const maxImpact =
        pastAgendas.length > 0
          ? Math.max(...pastAgendas.map((agenda) => agenda.impactScore || 0))
          : 0;

      const avgEScore =
        pastAgendas.length > 0
          ? Math.round(totalEScore / pastAgendas.length)
          : 0;

      setReportStats({
        eScore: avgEScore,
        maxImpactScore: maxImpact,
        totalAgendas: pastAgendas.length,
      });
    }
  }, [reportData, pastAgendas]);

  // Custom duplicate handler that redirects to agendas page
  const handleDuplicateAgenda = async () => {
    try {
      await duplicateAgenda(selectedAgendaId).unwrap();
      dispatch(showSuccessToast("Agenda duplicated successfully"));
      setIsDuplicateModalOpen(false);
      setSelectedAgendaId("");
      // Navigate to agendas page where user can see the duplicated agenda
      navigate(`${$Routes.allAgendaPage}?tab=active`);
    } catch (error) {
      dispatch(
        showErrorToast(error.data?.message || "Failed to duplicate agenda")
      );
    }
  };

  // Modal close handler
  const handleCloseDuplicateModal = () => {
    setIsDuplicateModalOpen(false);
    setSelectedAgendaId("");
  };

  const isLoading = isLoadingAgendas || isLoadingReport;

  return (
    <>
      <AgendaHeader
        title="Report Home"
        buttonText="New Smart Agenda"
        onSmartAgendaClick={() => navigate($Routes.smartAgendasPage)}
      />

      {/* Loading State */}
      {isLoading && (
        <Flex justify="center" align="center" py={8}>
          <Spinner size="lg" color="purple.500" />
          <Text ml={3} color="granite" fontSize="sm">
            Loading reports...
          </Text>
        </Flex>
      )}

      {/* No Data State */}
      {!isLoading && pastAgendas.length === 0 && (
        <Alert
          status="info"
          bg="blue.25"
          borderRadius="10px"
          border="1px solid"
          borderColor="#D0CCD5"
          p={6}
          textAlign="center"
        >
          <VStack spacing={3}>
            <Icon as={FiPieChart} boxSize={8} color="blue.500" />
            <Text
              fontSize="16px"
              fontWeight="600"
              color="#433358"
              fontFamily="Geist, sans-serif"
              mb={1}
            >
              No Reports Yet
            </Text>
            <AlertDescription
              fontSize="14px"
              color="#8B8DA6"
              fontFamily="Geist, sans-serif"
            >
              Complete your first meeting to see engagement reports and impact
              scores.
            </AlertDescription>
            <Button
              size="sm"
              colorScheme="blue"
              onClick={() => navigate(`${$Routes.allAgendaPage}?tab=active`)}
            >
              Create Your First Agenda
            </Button>
          </VStack>
        </Alert>
      )}

      {/* Stats and Agenda List */}
      {!isLoading && pastAgendas.length > 0 && (
        <>
          <Text fontSize="sm" p={4}>
            🌟🌟 Impact Score shows which Smart Agendas delivered the most value
            and which ones missed the mark.
          </Text>
          {/* Stats Section */}
          <ReportsStats
            eScore={reportStats.eScore}
            averageImpactScore={reportStats.maxImpactScore}
            showTrending={true}
          />

          <AgendaList
            agendas={pastAgendas}
            isPastAgenda={true}
            navigateToReports={true}
            setAgendaId={setSelectedAgendaId}
            setAgendaDeleteModalOpen={() => {}}
            setAgendaDuplicateModalOpen={setIsDuplicateModalOpen}
            onMenuToggle={() => {}}
          />
        </>
      )}

      {/* Duplicate Agenda Modal */}
      <AgendaModal
        title="Duplicate Agenda"
        body="Are you sure you want to duplicate this agenda?"
        icon={duplicateIcon}
        isOpen={isDuplicateModalOpen}
        isLoading={isDuplicatingAgenda}
        onClose={handleCloseDuplicateModal}
        handlePrimaryAction={handleDuplicateAgenda}
        primaryButtonText="Duplicate"
        handleSecondaryAction={handleCloseDuplicateModal}
      />
    </>
  );
};

export default ReportsView;
